import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TableSkeleton from '../TableSkeleton';
import EmptyTableIconV2 from '../../assets/images/EmtyTableV2';
import {
  BORDER_SOFT,
  BORDER_STRONG,
  CellContext,
  Density,
  EmptyState,
  HOVER,
  Icon,
  ListBulkAction,
  ListColumn,
  ListEmptyState,
  ListRowAction,
  NoResults,
  PAGE_SIZES,
  SelectBox,
  SortDir,
  TEAL,
  TEAL_DARK,
  TEXT,
  TEXT_DIM,
  TEXT_MUTED,
  cellValue,
  t,
} from './listShared';

export interface ListSelection {
  selectedIds: string[];
  onToggleRow: (id: string) => void;
  /** Header checkbox: select or clear every row on the CURRENT page. */
  onTogglePage: () => void;
  onClearSelection: () => void;
  /** When absent (or empty) no checkbox column renders. */
  bulkActions?: ListBulkAction[];
}

export interface ListTableProps<Row = any> {
  language: any;
  columns: ListColumn<Row>[];
  /** Raw entities from the endpoint. */
  rows: Row[];
  rowKey?: (row: Row) => string;
  loading: boolean;
  count: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  sortBy?: string;
  sortDir?: SortDir;
  onSortChange?: (sortBy: string, sortDir: SortDir) => void;
  selection?: ListSelection;
  /** Kebab menu entries. No kebab column when empty. */
  rowActions?: ListRowAction<Row>[];
  emptyState: ListEmptyState;
  /**
   * True while a search or filter is active, so an empty page shows the
   * kit's "No Results Found" block instead of the entity's empty state.
   */
  hasActiveQuery?: boolean;
  visibleColumns: Record<string, boolean>;
  density: Density;
  /**
   * Optional totals row pinned under the last row, keyed by column key (a
   * column without an entry renders an empty cell). Shown only with rows.
   */
  totalsRow?: Record<string, React.ReactNode>;
}

const ROW_HEIGHTS: Record<Density, number> = {
  compact: 44,
  default: 52,
  comfortable: 64,
};

const HEADER_CELL: React.CSSProperties = {
  fontSize: '12.5px',
  fontWeight: 600,
  letterSpacing: '.02em',
  color: TEXT_MUTED,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// Clickable controls are real buttons (keyboard and screen readers); this strips the browser's button look.
const BUTTON_RESET: React.CSSProperties = { background: 'none', border: 'none', padding: 0, margin: 0, lineHeight: 'inherit' };

const HEADER_BG = '#FAFBFB';
const ROW_BG = '#FFFFFF';
const CELL_GAP = 12;
const ROW_PADDING = 16;
const SELECT_WIDTH = 18;
const KEBAB_WIDTH = 20;
/** A column never shrinks below this unless it is narrower to begin with. */
const MIN_COLUMN_WIDTH = 72;

const defaultRowKey = (row: any) => String(row?.id);

const minWidthOf = (column: ListColumn) =>
  column.minWidth ?? Math.min(column.width, MIN_COLUMN_WIDTH);

/**
 * Column sizing: a column starts at its `width` and shrinks (with an
 * ellipsis) down to its minimum before anything clips. A screen whose widths
 * fit keeps them exactly.
 */
const columnStyle = (column: ListColumn): React.CSSProperties => ({
  flex: `0 1 ${column.width}px`,
  minWidth: `${minWidthOf(column)}px`,
  textAlign: column.align === 'right' ? 'right' : undefined,
});

/**
 * The desktop list table: sticky header, fixed-width columns, optional
 * selection column + bulk bar, kebab row menu and the pagination footer.
 * Header and rows share ONE scroll container: columns shrink to their
 * minimums first, and when even those do not fit the whole grid scrolls
 * horizontally with the kebab column pinned to the right edge.
 * Deliberately NOT built on the shared TableReportV2 (equal-width columns,
 * per-column search row) which ~20 legacy screens still depend on.
 */
const ListTable: React.FC<ListTableProps> = ({
  language,
  columns,
  rows,
  rowKey = defaultRowKey,
  loading,
  count,
  page,
  limit,
  onPageChange,
  onLimitChange,
  sortBy = '',
  sortDir = 'DESC',
  onSortChange,
  selection,
  rowActions = [],
  emptyState,
  hasActiveQuery = false,
  visibleColumns,
  density,
  totalsRow,
}) => {
  const [menuFor, setMenuFor] = useState<{ id: string; x: number; y: number } | null>(null);
  const [pageSizeOpen, setPageSizeOpen] = useState<boolean>(false);
  const pageSizeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (pageSizeRef.current && !pageSizeRef.current.contains(event.target as Node)) {
        setPageSizeOpen(false);
      }
      setMenuFor(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  /**
   * The row menu is a position:fixed portal anchored to a snapshot of the
   * row's rect. Scrolling the table body (or resizing) moves the row but not
   * the menu, leaving "Delete" floating next to a different row — so close it
   * on any scroll (capture phase catches the inner scroll container) and on
   * resize.
   */
  useEffect(() => {
    if (!menuFor) return;
    const close = () => setMenuFor(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menuFor]);

  const rowHeight = ROW_HEIGHTS[density] ?? ROW_HEIGHTS.default;
  const selectable = !!selection && (selection.bulkActions?.length ?? 0) > 0;
  const selectedIds = selection?.selectedIds ?? [];
  const selected = new Set(selectedIds.map(String));
  const pageIds = rows.map(rowKey);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const shownColumns = columns.filter(
    c => c.desktop !== false && (c.hideable === false || visibleColumns[c.key] !== false)
  );
  const hasMenu = rowActions.length > 0;
  const menuRowRecord = menuFor ? rows.find(r => rowKey(r) === menuFor.id) : undefined;

  /**
   * The narrowest the grid can get: every column at its minimum plus the
   * gaps, the padding and the fixed columns. Below this the table scrolls
   * sideways instead of clipping.
   */
  const cellCount = shownColumns.length + (selectable ? 1 : 0) + (hasMenu ? 1 : 0);
  const minGridWidth =
    shownColumns.reduce((sum, c) => sum + minWidthOf(c), 0) +
    (selectable ? SELECT_WIDTH : 0) +
    // The pinned cell is content-box: its leading CELL_GAP is padding, not a
    // flex gap, so it is counted here on top of the gap budget below.
    (hasMenu ? KEBAB_WIDTH + CELL_GAP : 0) +
    Math.max(0, cellCount - 1) * CELL_GAP +
    ROW_PADDING * 2;

  const totalPages = Math.max(1, Math.ceil(count / (limit || 50)));
  const startItem = count === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, count);

  const visiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (page <= 3) pages.push(2, 3, '…', totalPages);
    else if (page >= totalPages - 2) pages.push('…', totalPages - 2, totalPages - 1, totalPages);
    else pages.push('…', page - 1, page, page + 1, '…', totalPages);
    return pages;
  };

  const sortKeyOf = (column: ListColumn) =>
    column.sortable && onSortChange ? column.sortKey ?? column.key : '';

  /** Clicking a sortable header sorts by it, or flips direction when it is already the sort. */
  const headerClick = (column: ListColumn) => {
    const key = sortKeyOf(column);
    if (!key || !onSortChange) return;
    if (key === sortBy) onSortChange(key, sortDir === 'DESC' ? 'ASC' : 'DESC');
    else onSortChange(key, 'DESC');
  };

  const headerCell = (column: ListColumn) => {
    const key = sortKeyOf(column);
    const isSorted = !!key && key === sortBy;
    const style: React.CSSProperties = {
      ...HEADER_CELL,
      ...columnStyle(column),
      color: isSorted ? TEAL : TEXT_MUTED,
      cursor: key ? 'pointer' : 'default',
    };
    const label = (
      <>
        {t(language, column.label, column.label)}
        {isSorted ? (sortDir === 'DESC' ? ' ↓' : ' ↑') : ''}
      </>
    );
    if (!key) {
      return (
        <div key={column.key} className="font-inter" style={style}>
          {label}
        </div>
      );
    }
    return (
      <button
        key={column.key}
        type="button"
        className="font-inter"
        style={{ ...BUTTON_RESET, ...style, textAlign: column.align === 'right' ? 'right' : 'left' }}
        onClick={() => headerClick(column)}
      >
        {label}
      </button>
    );
  };

  const ctxFor = (column: ListColumn, index: number): CellContext => ({
    language,
    sortBy,
    sortDir,
    index,
    page,
    limit,
    column,
  });

  const actionLabel = (action: ListRowAction, row: any) =>
    typeof action.label === 'function' ? action.label(row) : t(language, action.label, action.label);

  const menuRow = (action: ListRowAction, row: any, marginTop?: string) => (
    <div
      key={action.key}
      className="cursor-pointer duration-300 w-full flex items-center gap-x-2"
      style={{ borderRadius: '4px', marginTop }}
      onClick={() => {
        setMenuFor(null);
        action.onRun(row);
      }}
    >
      {action.icon}
      <p className="text-[14px] leading-[20px] font-inter" style={{ color: TEXT_MUTED }}>
        {actionLabel(action, row)}
      </p>
    </div>
  );

  /**
   * The kebab column is pinned to the right edge while the grid scrolls
   * sideways. The negative margin / padding pair keeps the icon exactly
   * where it sits in an unscrolled row (16px from the edge) while the pinned
   * cell still covers the content passing beneath it.
   */
  const pinnedStyle: React.CSSProperties = {
    marginLeft: 'auto',
    flex: 'none',
    width: `${KEBAB_WIDTH}px`,
    boxSizing: 'content-box',
    paddingLeft: `${CELL_GAP}px`,
    paddingRight: `${ROW_PADDING}px`,
    marginRight: `-${ROW_PADDING}px`,
    position: 'sticky',
    right: 0,
    backgroundColor: 'inherit',
    zIndex: 1,
  };

  /** Prev / Next chevrons flanking the page pills, disabled at either end. */
  const pageArrow = (dir: 'prev' | 'next') => {
    const disabled = dir === 'prev' ? page <= 1 : page >= totalPages;
    const label = dir === 'prev' ? 'Previous page' : 'Next page';
    return (
      <button
        type="button"
        disabled={disabled}
        aria-label={t(language, label, label)}
        className="flex items-center justify-center"
        style={{
          ...BUTTON_RESET,
          width: '30px',
          height: '30px',
          borderRadius: '8px',
          opacity: disabled ? 0.4 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transform: dir === 'prev' ? 'rotate(180deg)' : undefined,
        }}
        onClick={() => {
          if (!disabled) onPageChange(dir === 'prev' ? page - 1 : page + 1);
        }}
      >
        <Icon name="chev" color={TEXT_MUTED} size={16} />
      </button>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {selectable && selectedIds.length > 0 && (
        <div
          className="flex items-center font-inter"
          style={{
            gap: '14px',
            height: '44px',
            padding: '0 16px',
            borderRadius: '10px',
            backgroundColor: TEAL_DARK,
            color: '#FFFFFF',
            fontSize: '13px',
            marginBottom: '14px',
            flex: 'none',
          }}
        >
          <span style={{ fontWeight: 600 }}>
            {selectedIds.length} {t(language, 'selected', 'selected')}
          </span>
          <span style={{ opacity: 0.5 }}>|</span>
          {selection!.bulkActions!.map(action => (
            <span
              key={action.key}
              className="cursor-pointer"
              onClick={() => action.onRun(selectedIds)}
            >
              {t(language, action.label, action.label)}
            </span>
          ))}
          <div
            className="flex items-center cursor-pointer"
            style={{ marginLeft: 'auto', gap: '6px', opacity: 0.8 }}
            onClick={selection!.onClearSelection}
          >
            <Icon name="close" color="#FFFFFF" size={14} />
            <span>{t(language, 'Clear', 'Clear')}</span>
          </div>
        </div>
      )}

      <div
        style={{
          border: `1px solid ${BORDER_SOFT}`,
          borderRadius: '12px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          backgroundColor: '#FFFFFF',
        }}
      >
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <div style={{ minWidth: `${minGridWidth}px` }}>
            <div
              className="flex items-center"
              style={{
                gap: `${CELL_GAP}px`,
                height: '44px',
                padding: `0 ${ROW_PADDING}px`,
                borderBottom: `1px solid ${BORDER_SOFT}`,
                backgroundColor: HEADER_BG,
                position: 'sticky',
                top: 0,
                zIndex: 2,
              }}
            >
              {selectable && (
                <div
                  style={{ width: `${SELECT_WIDTH}px`, flex: 'none' }}
                  className="cursor-pointer"
                  onClick={selection!.onTogglePage}
                >
                  <SelectBox checked={allOnPageSelected} />
                </div>
              )}
              {shownColumns.map(headerCell)}
              {hasMenu && <div style={{ ...pinnedStyle, height: '100%' }} />}
            </div>

            {loading ? (
              <TableSkeleton rows={8} columns={Math.max(2, shownColumns.length)} />
            ) : rows.length === 0 ? (
              <div style={{ padding: '24px' }}>
                {hasActiveQuery ? (
                  <NoResults language={language} />
                ) : (
                  <EmptyState state={emptyState} fallbackImg={<EmptyTableIconV2 />} />
                )}
              </div>
            ) : (
              rows.map((row: any, index: number) => {
                const id = rowKey(row);
                const isSelected = selectable && selected.has(id);
                return (
                  <div
                    key={id}
                    className="flex items-center"
                    style={{
                      gap: `${CELL_GAP}px`,
                      height: `${rowHeight}px`,
                      padding: `0 ${ROW_PADDING}px`,
                      borderBottom: `1px solid ${BORDER_SOFT}`,
                      backgroundColor: isSelected ? HOVER : ROW_BG,
                    }}
                    onMouseEnter={e => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = HOVER;
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = ROW_BG;
                    }}
                  >
                    {selectable && (
                      <div
                        style={{ width: `${SELECT_WIDTH}px`, flex: 'none' }}
                        className="cursor-pointer"
                        onClick={() => selection!.onToggleRow(id)}
                      >
                        <SelectBox checked={isSelected} />
                      </div>
                    )}
                    {shownColumns.map(column => (
                      <div
                        key={column.key}
                        className={column.onClick ? 'cursor-pointer' : undefined}
                        style={{
                          ...columnStyle(column),
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                        }}
                        onClick={column.onClick ? () => column.onClick!(row) : undefined}
                      >
                        {cellValue(column, row, ctxFor(column, index), 'desktop')}
                      </div>
                    ))}
                    {hasMenu && (
                      <div
                        style={{ ...pinnedStyle, height: '100%' }}
                        className="cursor-pointer flex items-center"
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => {
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          setMenuFor(
                            menuFor?.id === id
                              ? null
                              : { id, x: rect.right - ROW_PADDING, y: rect.bottom + 6 }
                          );
                        }}
                      >
                        <Icon name="kebab" size={20} />
                      </div>
                    )}
                  </div>
                );
              })
            )}
            {totalsRow && !loading && rows.length > 0 && (
              <div
                className="flex items-center font-inter"
                style={{
                  gap: `${CELL_GAP}px`,
                  height: `${rowHeight}px`,
                  padding: `0 ${ROW_PADDING}px`,
                  borderTop: `1px solid ${BORDER_SOFT}`,
                  backgroundColor: HEADER_BG,
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 1,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: TEXT,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {selectable && <div style={{ width: `${SELECT_WIDTH}px`, flex: 'none' }} />}
                {shownColumns.map(column => (
                  <div
                    key={column.key}
                    style={{
                      ...columnStyle(column),
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {totalsRow[column.key] ?? ''}
                  </div>
                ))}
                {hasMenu && <div style={{ ...pinnedStyle, height: '100%' }} />}
              </div>
            )}
          </div>
        </div>

        <div
          className="flex items-center"
          style={{ gap: '10px', padding: '12px 16px', marginTop: 'auto', flex: 'none' }}
        >
          <span style={{ fontSize: '13px', color: TEXT_DIM }} className="font-inter">
            {startItem.toLocaleString()}–{endItem.toLocaleString()} {t(language, 'of', 'of')}{' '}
            {count.toLocaleString()}
          </span>
          <div style={{ position: 'relative' }} ref={pageSizeRef}>
            <div
              className="flex items-center cursor-pointer font-inter"
              style={{
                gap: '6px',
                height: '30px',
                padding: '0 10px',
                border: `1px solid ${BORDER_STRONG}`,
                borderRadius: '8px',
                fontSize: '13px',
                color: TEXT,
              }}
              onClick={() => setPageSizeOpen(!pageSizeOpen)}
            >
              <span>
                {limit} / {t(language, 'page', 'page')}
              </span>
              <Icon name="chevd" size={14} />
            </div>
            {pageSizeOpen && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 'calc(100% + 6px)',
                  left: 0,
                  zIndex: 60,
                  backgroundColor: '#FFFFFF',
                  border: `1px solid ${BORDER_SOFT}`,
                  borderRadius: '10px',
                  padding: '6px',
                  boxShadow: '0 12px 32px rgba(15,118,121,.12)',
                }}
              >
                {PAGE_SIZES.map(size => (
                  <div
                    key={size}
                    className="cursor-pointer font-inter"
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      color: size === limit ? TEAL : TEXT,
                    }}
                    onClick={() => {
                      setPageSizeOpen(false);
                      onLimitChange(size);
                    }}
                  >
                    {size} / {t(language, 'page', 'page')}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex" style={{ marginLeft: 'auto', gap: '6px' }}>
            {pageArrow('prev')}
            {visiblePages().map((p, index) => {
              const style: React.CSSProperties = {
                width: '30px',
                height: '30px',
                borderRadius: '8px',
                fontSize: '13px',
                backgroundColor: p === page ? TEAL : 'transparent',
                color: p === page ? '#FFFFFF' : TEXT_MUTED,
              };
              // The '…' gap is not a page: plain text, not a button.
              if (typeof p !== 'number') {
                return (
                  <span key={`page-${index}`} className="flex items-center justify-center font-inter" style={style}>
                    {p}
                  </span>
                );
              }
              return (
                <button
                  key={`page-${index}`}
                  type="button"
                  aria-label={`${t(language, 'Page', 'Page')} ${p}`}
                  aria-current={p === page ? 'page' : undefined}
                  className="flex items-center justify-center font-inter cursor-pointer"
                  style={{ ...BUTTON_RESET, ...style }}
                  onClick={() => {
                    if (p !== page) onPageChange(p);
                  }}
                >
                  {p}
                </button>
              );
            })}
            {pageArrow('next')}
          </div>
        </div>
      </div>

      {menuFor &&
        menuRowRecord &&
        createPortal(
          <div
            className="drop-shadow-config rounded-lg p-3 bg-white shadow_option space-y-1"
            style={{
              position: 'fixed',
              left: menuFor.x,
              top: menuFor.y,
              transform: 'translateX(-100%)',
              width: '147px',
              border: `1px solid ${BORDER_SOFT}`,
              borderRadius: '12px',
              zIndex: 300,
            }}
            onMouseDown={e => e.stopPropagation()}
          >
            {rowActions
              .filter(action => !action.hidden || !action.hidden(menuRowRecord))
              .map((action, index) =>
                menuRow(action, menuRowRecord, index === 0 ? undefined : '12px')
              )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ListTable;
